/**
 * 照片滤镜（对比 Photoshop 补全第一档缺口 阶段 1）。
 *
 * 语义：把像素往一个目标色（滤镜颜色）按浓度线性插值；勾选"保留明度"时
 * 按亮度比例把结果缩放回原始亮度，避免整体变暗/变亮。
 */
export interface PhotoFilterParams {
  /** 滤镜目标颜色，0–255。 */
  readonly r: number;
  readonly g: number;
  readonly b: number;
  /** 浓度，0–100。 */
  readonly density: number;
  /** 保留明度，0 或 1。 */
  readonly preserveLuminosity: number;
}

/** 恒等参数：浓度 0，滤镜颜色对结果无影响。 */
export const DEFAULT_PHOTO_FILTER_PARAMS: PhotoFilterParams = {
  r: 255,
  g: 157,
  b: 0,
  density: 0,
  preserveLuminosity: 1,
};

function clampByte(v: number): number {
  return Math.round(Math.min(255, Math.max(0, v)));
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function applyPhotoFilter(
  r: number,
  g: number,
  b: number,
  params: PhotoFilterParams,
): [number, number, number] {
  const t = Math.min(100, Math.max(0, params.density)) / 100;
  if (t === 0) return [r, g, b];
  let outR = r + (params.r - r) * t;
  let outG = g + (params.g - g) * t;
  let outB = b + (params.b - b) * t;
  if (params.preserveLuminosity !== 0) {
    const before = luminance(r, g, b);
    const after = luminance(outR, outG, outB);
    if (after > 1e-6) {
      const scale = before / after;
      outR *= scale;
      outG *= scale;
      outB *= scale;
    }
  }
  return [clampByte(outR), clampByte(outG), clampByte(outB)];
}
